import { useCallback, useMemo, useState } from 'react';
import {
  CodeLink,
  CodeRepoReviewStatus,
  Content,
  isCodeRepoReviewStatus,
  isRepoType,
  RepoType,
} from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { isForInternalContentAudience } from '../workflow-viewer/content/ContentUtils';
import {
  ValidationResult,
  validateAppsecTicketUrl,
  validateBranch,
  validateLabel,
  validateOpenSourceTicketUrl,
  validateRepoUrl,
} from '../../services/CodeRepoValidation';
import { useAudience } from '../v2-hooks/useAudience';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

// The backend accepts at most one code link per content
const MAX_CODE_LINKS = 1;

export type { ValidationResult } from '../../services/CodeRepoValidation';

export interface CodeRepoRow {
  readonly index: number;
  readonly url: string;
  readonly label: string;
  readonly branch: string;
  readonly repoType: RepoType;
  readonly appsecTicket: string;
  readonly appsecStatus: CodeRepoReviewStatus;
  readonly appsecGuardian: string;
  readonly openSourceTicket: string;
  readonly openSourceStatus: CodeRepoReviewStatus;
}

export interface CodeRepoFormErrors {
  readonly url: ValidationResult;
  readonly label: ValidationResult;
  readonly branch: ValidationResult;
  readonly appsecTicket: ValidationResult;
  readonly openSourceTicket: ValidationResult;
}

interface CodeRepoModalFields {
  readonly url: string;
  readonly label: string;
  readonly branch: string;
  readonly repoType: RepoType;
  readonly appsecTicket: string;
  readonly appsecStatus: CodeRepoReviewStatus;
  readonly appsecGuardian: string;
  readonly openSourceTicket: string;
  readonly openSourceStatus: CodeRepoReviewStatus;
  readonly errors: CodeRepoFormErrors;
  readonly isSaveEnabled: boolean;
}

export type CodeRepoModalState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'adding'; readonly fields: CodeRepoModalFields }
  | { readonly kind: 'editing'; readonly row: CodeRepoRow; readonly fields: CodeRepoModalFields };

export type DeleteConfirmationState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'confirming'; readonly row: CodeRepoRow };

export interface CodeRepoTabViewModel {
  readonly rows: readonly CodeRepoRow[];
  readonly canAddRepo: boolean;

  readonly modal: CodeRepoModalState;
  readonly openAddModal: () => void;
  readonly openEditModal: (row: CodeRepoRow) => void;
  readonly dismissModal: () => void;
  readonly saveModal: () => void;

  readonly changeUrl: (value: string) => void;
  readonly changeLabel: (value: string) => void;
  readonly changeBranch: (value: string) => void;
  readonly changeAppsecTicket: (value: string) => void;
  readonly changeAppsecStatus: (status: CodeRepoReviewStatus) => void;
  readonly changeAppsecGuardian: (value: string) => void;
  readonly changeOpenSourceTicket: (value: string) => void;
  readonly changeOpenSourceStatus: (status: CodeRepoReviewStatus) => void;

  readonly deleteConfirmation: DeleteConfirmationState;
  readonly requestDelete: (row: CodeRepoRow) => void;
  readonly confirmDelete: () => void;
  readonly cancelDelete: () => void;

  readonly isExternalAudience: boolean;
  readonly repoType: RepoType;
}

const VALID: ValidationResult = { valid: true };

function toRepoRow(codeLink: CodeLink, index: number): CodeRepoRow {
  return {
    index,
    url: codeLink.url ?? '',
    label: codeLink.label ?? '',
    branch: codeLink.branch ?? '',
    repoType:
      codeLink.repoType && isRepoType(codeLink.repoType) ? codeLink.repoType : RepoType.Internal,
    appsecTicket: codeLink.appsecTicket ?? '',
    appsecStatus:
      codeLink.appsecStatus && isCodeRepoReviewStatus(codeLink.appsecStatus)
        ? codeLink.appsecStatus
        : CodeRepoReviewStatus.NotStarted,
    appsecGuardian: codeLink.appsecGuardian ?? '',
    openSourceTicket: codeLink.openSourceTicket ?? '',
    openSourceStatus:
      codeLink.openSourceStatus && isCodeRepoReviewStatus(codeLink.openSourceStatus)
        ? codeLink.openSourceStatus
        : CodeRepoReviewStatus.NotStarted,
  };
}

function setCodeLinks(content: Content, codeLinks: CodeLink[]): Content {
  return {
    ...content,
    contentTypeMetadata: { ...content.contentTypeMetadata, codeLinks },
  };
}

/** Locate the code link matching `row` in the live array. Checks the captured index first; falls back to URL match if the array shifted. */
function findCodeLinkIndex(codeLinks: readonly CodeLink[], row: CodeRepoRow): number {
  if (row.index < codeLinks.length && codeLinks[row.index]?.url === row.url) {
    return row.index;
  }
  return codeLinks.findIndex((link) => link.url === row.url);
}

function buildCodeLink(fields: CodeRepoModalFields): CodeLink {
  const link = CodeLink.create(fields.url.trim(), fields.label.trim() || undefined);
  link.branch = fields.branch.trim() || undefined;
  link.repoType = fields.repoType;
  link.appsecTicket = fields.appsecTicket.trim() || undefined;
  link.appsecStatus = fields.appsecStatus;
  link.appsecGuardian = fields.appsecGuardian.trim() || undefined;
  link.openSourceTicket = fields.openSourceTicket.trim() || undefined;
  link.openSourceStatus = fields.openSourceStatus;
  return link;
}

function computeErrors(fields: CodeRepoModalFields, isExternal: boolean): CodeRepoFormErrors {
  return {
    url: fields.url.trim() ? validateRepoUrl(fields.url, fields.repoType) : VALID,
    label: validateLabel(fields.label),
    branch: validateBranch(fields.branch),
    appsecTicket: fields.appsecTicket.trim() ? validateAppsecTicketUrl(fields.appsecTicket) : VALID,
    openSourceTicket:
      isExternal && fields.openSourceTicket.trim()
        ? validateOpenSourceTicketUrl(fields.openSourceTicket)
        : VALID,
  };
}

function computeIsSaveEnabled(
  fields: CodeRepoModalFields,
  errors: CodeRepoFormErrors,
  isExternal: boolean,
): boolean {
  // Required fields must be non-empty
  if (!fields.url.trim()) return false;

  // All validated fields must pass
  if (!errors.url.valid) return false;
  if (!errors.label.valid) return false;
  if (!errors.branch.valid) return false;
  if (!errors.appsecTicket.valid) return false;
  if (isExternal && !errors.openSourceTicket.valid) return false;

  return true;
}

function createDefaultFields(repoType: RepoType): CodeRepoModalFields {
  const fields: CodeRepoModalFields = {
    url: '',
    label: '',
    branch: '',
    repoType,
    appsecTicket: '',
    appsecStatus: CodeRepoReviewStatus.NotStarted,
    appsecGuardian: '',
    openSourceTicket: '',
    openSourceStatus: CodeRepoReviewStatus.NotStarted,
    errors: {
      url: VALID,
      label: VALID,
      branch: VALID,
      appsecTicket: VALID,
      openSourceTicket: VALID,
    },
    isSaveEnabled: false,
  };
  return fields;
}

function createFieldsFromRow(row: CodeRepoRow, isExternal: boolean): CodeRepoModalFields {
  const partial: CodeRepoModalFields = {
    url: row.url,
    label: row.label,
    branch: row.branch,
    // Follow the content audience, not the stored link type, because the backend validates by audience
    repoType: isExternal ? RepoType.External : RepoType.Internal,
    appsecTicket: row.appsecTicket,
    appsecStatus: row.appsecStatus,
    appsecGuardian: row.appsecGuardian,
    openSourceTicket: row.openSourceTicket,
    openSourceStatus: row.openSourceStatus,
    errors: {
      url: VALID,
      label: VALID,
      branch: VALID,
      appsecTicket: VALID,
      openSourceTicket: VALID,
    },
    isSaveEnabled: false,
  };
  const errors = computeErrors(partial, isExternal);
  return { ...partial, errors, isSaveEnabled: computeIsSaveEnabled(partial, errors, isExternal) };
}

export function useCodeRepoTabViewModel(): CodeRepoTabViewModel | undefined {
  const service = useContentEditorService();
  const content = useContent();
  const audience = useAudience();
  const document = useLoadedDocument();

  // Defaults to internal while audience is unloaded; the teardown guard below
  // returns undefined before any of these values reach the view.
  const isExternal = audience ? !isForInternalContentAudience(audience) : false;

  const [modal, setModal] = useState<CodeRepoModalState>({ kind: 'closed' });
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    kind: 'idle',
  });

  // When content changes, then recompute table rows from code links
  const rows: readonly CodeRepoRow[] = useMemo(() => {
    const codeLinks = content?.contentTypeMetadata?.codeLinks ?? [];
    return codeLinks.map(toRepoRow);
  }, [content]);

  const canAddRepo = rows.length < MAX_CODE_LINKS;

  const openAddModal = useCallback(() => {
    const defaultRepoType = isExternal ? RepoType.External : RepoType.Internal;
    setModal({ kind: 'adding', fields: createDefaultFields(defaultRepoType) });
  }, [isExternal]);

  const openEditModal = useCallback(
    (row: CodeRepoRow) => {
      setModal({ kind: 'editing', row, fields: createFieldsFromRow(row, isExternal) });
    },
    [isExternal],
  );

  const dismissModal = useCallback(() => {
    setModal({ kind: 'closed' });
  }, []);

  const saveModal = useCallback(() => {
    if (modal.kind === 'closed') return;
    if (!modal.fields.isSaveEnabled) return;

    const current = service.content;
    if (!current) throw new Error('content is unexpectedly closed');

    const codeLinks = [...(current.contentTypeMetadata?.codeLinks ?? [])];
    const newLink = buildCodeLink(modal.fields);

    if (modal.kind === 'adding') {
      codeLinks.push(newLink);
    } else {
      const idx = findCodeLinkIndex(codeLinks, modal.row);
      if (idx < 0) return;
      codeLinks[idx] = newLink;
    }

    service.stageEdit(setCodeLinks(current, codeLinks));
    setModal({ kind: 'closed' });
  }, [modal, service]);

  const updateFields = useCallback(
    (updater: (prev: CodeRepoModalFields) => Partial<CodeRepoModalFields>) => {
      setModal((prev) => {
        if (prev.kind === 'closed') return prev;
        const patch = updater(prev.fields);
        const nextFields = { ...prev.fields, ...patch };
        const errors = computeErrors(nextFields, isExternal);
        const isSaveEnabled = computeIsSaveEnabled(nextFields, errors, isExternal);
        const fields = { ...nextFields, errors, isSaveEnabled };
        if (prev.kind === 'adding') return { kind: 'adding', fields };
        return { kind: 'editing', row: prev.row, fields };
      });
    },
    [isExternal],
  );

  const changeUrl = useCallback(
    (value: string) => updateFields(() => ({ url: value })),
    [updateFields],
  );
  const changeLabel = useCallback(
    (value: string) => updateFields(() => ({ label: value })),
    [updateFields],
  );
  const changeBranch = useCallback(
    (value: string) => updateFields(() => ({ branch: value })),
    [updateFields],
  );
  const changeAppsecTicket = useCallback(
    (value: string) => updateFields(() => ({ appsecTicket: value })),
    [updateFields],
  );
  const changeAppsecStatus = useCallback(
    (status: CodeRepoReviewStatus) => updateFields(() => ({ appsecStatus: status })),
    [updateFields],
  );
  const changeAppsecGuardian = useCallback(
    (value: string) => updateFields(() => ({ appsecGuardian: value })),
    [updateFields],
  );
  const changeOpenSourceTicket = useCallback(
    (value: string) => updateFields(() => ({ openSourceTicket: value })),
    [updateFields],
  );
  const changeOpenSourceStatus = useCallback(
    (status: CodeRepoReviewStatus) => updateFields(() => ({ openSourceStatus: status })),
    [updateFields],
  );

  const requestDelete = useCallback((row: CodeRepoRow) => {
    setDeleteConfirmation({ kind: 'confirming', row });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirmation.kind !== 'confirming') return;

    const current = service.content;
    if (!current) throw new Error('content is unexpectedly closed');

    const codeLinks = [...(current.contentTypeMetadata?.codeLinks ?? [])];
    const idx = findCodeLinkIndex(codeLinks, deleteConfirmation.row);
    if (idx < 0) return;
    codeLinks.splice(idx, 1);
    service.stageEdit(setCodeLinks(current, codeLinks));
    setDeleteConfirmation({ kind: 'idle' });
  }, [deleteConfirmation, service]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation({ kind: 'idle' });
  }, []);

  const repoType = isExternal ? RepoType.External : RepoType.Internal;

  // A parent-teardown render can transiently clear content/audience/handle
  // before this view unmounts; return undefined so the view renders null
  // instead of throwing.
  if (!content || !audience || !document) return undefined;

  return {
    rows,
    canAddRepo,
    modal,
    openAddModal,
    openEditModal,
    dismissModal,
    saveModal,
    changeUrl,
    changeLabel,
    changeBranch,
    changeAppsecTicket,
    changeAppsecStatus,
    changeAppsecGuardian,
    changeOpenSourceTicket,
    changeOpenSourceStatus,
    deleteConfirmation,
    requestDelete,
    confirmDelete,
    cancelDelete,
    isExternalAudience: isExternal,
    repoType,
  };
}
